import React, { useState } from 'react';
import './UrlCard.css';

function UrlCard({ url, onDelete, onUpdateTags, onUpdateDescription, onToggleStar, availableTags }) {
  const [isEditing, setIsEditing] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionInput, setDescriptionInput] = useState(url.description || '');

  const handleAddTag = (e) => {
    e.preventDefault();
    if (tagInput.trim()) {
      const newTags = [...url.tags, tagInput.trim()];
      onUpdateTags(url.id, newTags);
      setTagInput('');
      setIsEditing(false);
    }
  };

  const handleUpdateDescription = (e) => {
    e.preventDefault();
    onUpdateDescription(url.id, descriptionInput);
    setIsEditingDescription(false);
  };

  const handleCancelDescription = () => {
    setDescriptionInput(url.description || '');
    setIsEditingDescription(false);
  };

  const handleQuickAddTag = (tagName) => {
    if (!url.tags.includes(tagName)) {
      const newTags = [...url.tags, tagName];
      onUpdateTags(url.id, newTags);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = url.tags.filter(tag => tag !== tagToRemove);
    onUpdateTags(url.id, newTags);
  };

  const getUnusedTags = () => {
    if (!availableTags) return [];
    return availableTags
      .filter(tag => !url.tags.includes(tag.name))
      .slice(0, 5); // Show max 5 suggestions
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const shortenUrl = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      const domain = urlObj.hostname.replace('www.', '');
      const path = urlObj.pathname + urlObj.search;
      
      if (path.length > 40) {
        return `${domain}${path.substring(0, 40)}...`;
      }
      return domain + path;
    } catch {
      return urlString.length > 50 ? urlString.substring(0, 50) + '...' : urlString;
    }
  };

  return (
    <div className="url-card">
      <div className="url-card-thumbnail">
        <img 
          src={url.thumbnail} 
          alt={url.title}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.classList.add('no-thumbnail');
          }}
        />
      </div>
      
      <div className="url-card-content">
        <h3 className="url-card-title">
          <a href={url.url} target="_blank" rel="noopener noreferrer">
            {url.title}
          </a>
        </h3>
        
        <div className="url-card-description-container">
          {isEditingDescription ? (
            <form onSubmit={handleUpdateDescription} className="description-edit-form">
              <textarea
                value={descriptionInput}
                onChange={(e) => setDescriptionInput(e.target.value)}
                placeholder="Enter description"
                className="description-input"
                autoFocus
                rows={3}
              />
              <div className="description-buttons">
                <button type="submit" className="description-save-btn">Save</button>
                <button 
                  type="button" 
                  className="description-cancel-btn"
                  onClick={handleCancelDescription}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              {url.description && (
                <p className="url-card-description">{url.description}</p>
              )}
              <button 
                className="description-edit-btn"
                onClick={() => setIsEditingDescription(true)}
                title="Edit description"
              >
                {url.description ? '✎ Edit' : '+ Add description'}
              </button>
            </>
          )}
        </div>
        
        <div className="url-card-url">
          <small title={url.url}>{shortenUrl(url.url)}</small>
        </div>
        
        <div className="url-card-tags">
          {url.tags && url.tags.map((tag, index) => (
            <span key={index} className="tag">
              {tag}
              <button 
                className="tag-remove"
                onClick={() => handleRemoveTag(tag)}
                title="Remove tag"
              >
                ×
              </button>
            </span>
          ))}

          {getUnusedTags().length > 0 && (
            <div className="quick-add-tags">
              <small>Quick add:</small>
              {getUnusedTags().map((tag, index) => (
                <button
                  key={index}
                  className="quick-add-tag"
                  onClick={() => handleQuickAddTag(tag.name)}
                  title={`Add ${tag.name} tag`}
                >
                  + {tag.name}
                </button>
              ))}
            </div>
          )}
          
          {isEditing ? (
            <form onSubmit={handleAddTag} className="tag-input-form">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add tag"
                className="tag-input"
                autoFocus
              />
              <button type="submit" className="tag-add-btn">✓</button>
              <button 
                type="button" 
                className="tag-cancel-btn"
                onClick={() => {
                  setIsEditing(false);
                  setTagInput('');
                }}
              >
                ×
              </button>
            </form>
          ) : (
            <button 
              className="tag-add"
              onClick={() => setIsEditing(true)}
              title="Add tag"
            >
              + Add tag
            </button>
          )}
        </div>
        
        <div className="url-card-footer">
          <span className="url-card-date">{formatDate(url.created_at)}</span>
          <div className="url-card-actions">
            <button 
              className={`url-card-star ${url.starred ? 'starred' : ''}`}
              onClick={() => onToggleStar(url.id, !url.starred)}
              title={url.starred ? 'Unstar' : 'Star'}
            >
              {url.starred ? '★' : '☆'} Star
            </button>
            <button 
              className="url-card-delete"
              onClick={() => {
                if (window.confirm('Delete this bookmark?')) {
                  onDelete(url.id);
                }
              }}
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UrlCard;
